import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Messages } from "primereact/messages";
import { ProgressBar } from "primereact/progressbar";
import Container from "react-bootstrap/Container";
import { useAuth } from "../contexts/AuthContext";
import walletApi from "../api/walletApi";
import { getSupportLocations } from "../api/membersApi";
import AccessState from "./AccessState";
import usePageAccessRedirect from "../hooks/usePageAccessRedirect";
import "./SupportWalletBranding.css";

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const SUPPORTED_LOGO_TYPES = ["image/png", "image/jpeg"];
const DEFAULT_PASS_TITLE = "Smart Tile Access Pass";
const TEMPLATE_TAGS =
  "%location name%, %serial number%, %expiry utc%, %member number%, %uses remaining%";
const editorModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }]
  ]
};
const durationParts = totalMinutes => {
  const value = Number(totalMinutes) || 0;
  return {
    days: Math.floor(value / 1440),
    hours: Math.floor((value % 1440) / 60),
    minutes: value % 60
  };
};

const SupportWalletBranding = () => {
  const { accessContext } = useAuth();
  const customerId = Number(
    accessContext && accessContext.claims && accessContext.claims.customerId
  );
  const navigate = useNavigate();
  const msgs = useRef(null);
  const {
    accessIssue,
    clearAccessIssue,
    handleAccessError
  } = usePageAccessRedirect({
    deniedTitle: "Wallet Settings Unavailable",
    deniedMessage:
      "You are not authorized to manage Apple Wallet Passes for this customer.",
    deniedRedirectTo: "/support"
  });
  const [availability, setAvailability] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingGoogleLogo, setUploadingGoogleLogo] = useState(false);
  const [uploadingPassImage, setUploadingPassImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [showPassPreviewDialog, setShowPassPreviewDialog] = useState(false);
  const [showTestPassDialog, setShowTestPassDialog] = useState(false);
  const [sendingTestPass, setSendingTestPass] = useState(false);
  const [testPassResult, setTestPassResult] = useState(null);
  const [testPassRequest, setTestPassRequest] = useState({
    locationId: null,
    email: localStorage.getItem("userEmail") || "",
    expiresAtUtc: "",
    overrideLms: true,
    memberNumber: ""
  });

  useEffect(() => {
    const load = async () => {
      try {
        clearAccessIssue();
        setLoadError("");
        const result = await walletApi.getSupportCustomerSettings(customerId);
        const duration = durationParts(result.maxDurationMinutes);
        setAvailability({
          ...result,
          backgroundColor: result.backgroundColor || "#ffffff",
          wordingColor: result.wordingColor || "#FFFFFF",
          logoUrl: result.logoUrl || "",
          googleLogoUrl: result.googleLogoUrl || "",
          passImageUrl: result.passImageUrl || "",
          passTitle: result.passTitle || DEFAULT_PASS_TITLE,
          emailSubject: result.emailSubject || "",
          emailBody: result.emailBody || "",
          limitByMaxUses: Boolean(result.limitByMaxUses),
          maxUsesLimit: result.maxUsesLimit || null,
          limitByMaxDuration: Boolean(result.limitByMaxDuration),
          durationMinutes: duration.minutes,
          durationHours: duration.hours,
          durationDays: duration.days
        });
      } catch (error) {
        if (handleAccessError(error)) return;
        if (error && error.response && error.response.status === 404) {
          setLoadError("Customer not found.");
        } else if (msgs.current) {
          msgs.current.show({
            severity: "error",
            summary: "",
            detail: "Unable to load Wallet Passes settings.",
            sticky: true
          });
        }
      }
    };
    load();
  }, [customerId, clearAccessIssue, handleAccessError]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocationsLoading(true);
        const appProviderId =
          accessContext && accessContext.claims
            ? accessContext.claims.appProviderId
            : null;
        setLocations(await getSupportLocations(customerId, appProviderId));
      } catch (error) {
        if (!handleAccessError(error) && msgs.current)
          msgs.current.show({
            severity: "error",
            summary: "",
            detail: "Unable to load customer locations.",
            sticky: true
          });
      } finally {
        setLocationsLoading(false);
      }
    };
    if (Number.isFinite(customerId) && customerId > 0) loadLocations();
  }, [accessContext, customerId, handleAccessError]);

  const updateSetting = (field, value) =>
    setAvailability({ ...availability, [field]: value });

  const showSaveDialog = (severity, message) =>
    setSaveDialog({ severity, message });

  const upload = async event => {
    const file = event.files && event.files[0];
    if (!file) return;
    if (
      SUPPORTED_LOGO_TYPES.indexOf(file.type) === -1 ||
      file.size > MAX_LOGO_SIZE
    ) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Logo must be a PNG, JPG, or JPEG file no larger than 2 MB."
      });
      return;
    }
    try {
      setUploading(true);
      const result = await walletApi.uploadLogo(customerId, file);
      setAvailability(current => ({ ...current, logoUrl: result.logoUrl }));
      msgs.current.show({
        severity: "success",
        summary: "",
        detail: "Apple Wallet logo uploaded successfully."
      });
    } catch (error) {
      if (!handleAccessError(error) && msgs.current)
        msgs.current.show({
          severity: "error",
          summary: "",
          detail: "Logo upload failed.",
          sticky: true
        });
    } finally {
      setUploading(false);
    }
  };

  const uploadGoogleLogo = async event => {
    const file = event.files && event.files[0];
    if (!file) return;
    if (
      SUPPORTED_LOGO_TYPES.indexOf(file.type) === -1 ||
      file.size > MAX_LOGO_SIZE
    ) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Google Wallet logo must be a PNG, JPG, or JPEG file no larger than 2 MB."
      });
      return;
    }
    try {
      setUploadingGoogleLogo(true);
      const result = await walletApi.uploadGoogleLogo(customerId, file);
      setAvailability(current => ({
        ...current,
        googleLogoUrl: result.googleLogoUrl
      }));
      msgs.current.show({
        severity: "success",
        summary: "",
        detail: "Google Wallet logo uploaded successfully."
      });
    } catch (error) {
      if (!handleAccessError(error) && msgs.current)
        msgs.current.show({
          severity: "error",
          summary: "",
          detail: "Google Wallet logo upload failed.",
          sticky: true
        });
    } finally {
      setUploadingGoogleLogo(false);
    }
  };

  const uploadPassImage = async event => {
    const file = event.files && event.files[0];
    if (!file) return;
    if (
      SUPPORTED_LOGO_TYPES.indexOf(file.type) === -1 ||
      file.size > MAX_LOGO_SIZE
    ) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Pass image must be a PNG, JPG, or JPEG file no larger than 2 MB."
      });
      return;
    }
    try {
      setUploadingPassImage(true);
      const result = await walletApi.uploadPassImage(customerId, file);
      setAvailability({ ...availability, passImageUrl: result.passImageUrl });
      msgs.current.show({
        severity: "success",
        summary: "",
        detail: "Pass image uploaded successfully."
      });
    } catch (error) {
      if (!handleAccessError(error) && msgs.current)
        msgs.current.show({
          severity: "error",
          summary: "",
          detail: "Pass image upload failed.",
          sticky: true
        });
    } finally {
      setUploadingPassImage(false);
    }
  };

  const save = async () => {
    const maxDurationMinutes =
      Number(availability.durationMinutes || 0) +
      Number(availability.durationHours || 0) * 60 +
      Number(availability.durationDays || 0) * 1440;
    if (
      availability.limitByMaxUses &&
      (!Number.isInteger(Number(availability.maxUsesLimit)) ||
        Number(availability.maxUsesLimit) <= 0)
    ) {
      showSaveDialog("error", "Maximum number of uses must be a positive integer.");
      return;
    }
    if (availability.limitByMaxDuration && maxDurationMinutes <= 0) {
      showSaveDialog("error", "Maximum time must be greater than zero.");
      return;
    }
    if (!HEX_COLOR_PATTERN.test(availability.backgroundColor || "")) {
      showSaveDialog("error", "Background color must be a valid #RRGGBB value.");
      return;
    }
    if (!HEX_COLOR_PATTERN.test(availability.wordingColor || "")) {
      showSaveDialog("error", "Wording color must be a valid #RRGGBB value.");
      return;
    }
    if (
      availability.googleLogoUrl &&
      !/^https:\/\//i.test(availability.googleLogoUrl)
    ) {
      showSaveDialog("error", "Google Wallet logo must use a public HTTPS URL.");
      return;
    }
    if (!availability.passTitle || availability.passTitle.length > 200) {
      showSaveDialog("error", "Wallet pass title is required and cannot exceed 200 characters.");
      return;
    }
    if (!availability.emailSubject || availability.emailSubject.length > 200) {
      showSaveDialog("error", "Email subject is required and cannot exceed 200 characters.");
      return;
    }
    if (!availability.emailBody || availability.emailBody.length > 4000) {
      showSaveDialog("error", "Email body is required and cannot exceed 4000 characters.");
      return;
    }
    try {
      setSaving(true);
      const saved = await walletApi.saveSupportCustomerSettings(customerId, {
        isEnabled: Boolean(availability.isEnabled),
        backgroundColor: availability.backgroundColor,
        wordingColor: availability.wordingColor,
        logoUrl: availability.logoUrl || null,
        googleLogoUrl: availability.googleLogoUrl || null,
        passImageUrl: availability.passImageUrl || null,
        passTitle: availability.passTitle,
        emailSubject: availability.emailSubject,
        emailBody: availability.emailBody,
        limitByMaxUses: Boolean(availability.limitByMaxUses),
        maxUsesLimit: availability.limitByMaxUses
          ? Number(availability.maxUsesLimit)
          : null,
        limitByMaxDuration: Boolean(availability.limitByMaxDuration),
        maxDurationMinutes: availability.limitByMaxDuration
          ? maxDurationMinutes
          : null
      });
      setAvailability({ ...availability, ...saved });
      showSaveDialog("success", "Wallet Passes settings saved successfully.");
    } catch (error) {
      if (!handleAccessError(error))
        showSaveDialog("error", "Failed to save Wallet Passes settings.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestPass = async () => {
    const email = String(testPassRequest.email || "").trim();
    if (
      !Number.isFinite(Number(testPassRequest.locationId)) ||
      Number(testPassRequest.locationId) <= 0
    ) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Please select a location."
      });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Please enter a valid email address."
      });
      return;
    }
    if (
      !testPassRequest.overrideLms &&
      !String(testPassRequest.memberNumber || "").trim()
    ) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Member/Card Number is required when Override LMS is disabled."
      });
      return;
    }
    try {
      setSendingTestPass(true);
      setTestPassResult(null);
      const result = await walletApi.sendSupportCustomerTestPass(customerId, {
        locationId: Number(testPassRequest.locationId),
        email,
        expiresAtUtc: testPassRequest.expiresAtUtc
          ? new Date(testPassRequest.expiresAtUtc).toISOString()
          : null,
        overrideLms: Boolean(testPassRequest.overrideLms),
        memberNumber: testPassRequest.overrideLms
          ? null
          : String(testPassRequest.memberNumber).trim()
      });
      setTestPassResult(result);
      msgs.current.show({
        severity: "success",
        summary: "",
        detail: "Test wallet pass email sent."
      });
    } catch (error) {
      if (handleAccessError(error)) return;
      const status = error && error.response && error.response.status;
      const data = error && error.response && error.response.data;
      const backendMessage =
        typeof data === "string" ? data : data && (data.detail || data.title);
      const detail =
        status === 404
          ? "Customer not found."
          : status === 500
          ? "The test wallet pass could not be generated. Please check wallet configuration."
          : backendMessage || "Failed to send test wallet pass email.";
      if (msgs.current)
        msgs.current.show({
          severity: "error",
          summary: "",
          detail,
          sticky: true
        });
    } finally {
      setSendingTestPass(false);
    }
  };

  if (accessIssue) return <AccessState {...accessIssue} />;
  return (
    <Card className="justify-content-md-center mt-5 wallet-branding-page">
      <Container fluid="md">
        <div className="wallet-branding-header">
          <Button
            link
            icon="pi pi-arrow-left"
            onClick={() => navigate("/support")}
          />
          <div>
            <h4>Wallet Passes</h4>
            <p>Configure your pass appearance and email content.</p>
          </div>
        </div>
        <Messages ref={msgs} />
        {availability === null ? (
          loadError ? (
            <p>{loadError}</p>
          ) : (
            <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
          )
        ) : (
          <div className="p-fluid p-formgrid p-grid wallet-branding-form">
            {!availability.isEnabled && (
              <p className="p-col-12 wallet-branding-notice">
                Apple Wallet Passes are not enabled for this customer.
              </p>
            )}
            <div className="p-field p-col-12 mb-3">
              <label htmlFor="walletPassTitle" className="d-block mb-2">
                Wallet pass title
              </label>
              <InputText
                id="walletPassTitle"
                value={availability.passTitle}
                maxLength={200}
                onChange={e => updateSetting("passTitle", e.target.value)}
              />
              <small>This title appears on Apple Wallet and Google Wallet passes.</small>
              <br />
              <small>{availability.passTitle.length}/200</small>
            </div>
            <div className="p-col-12 mb-3 wallet-branding-appearance">
              <div className="wallet-branding-color-controls">
                <div className="p-field">
                  <label htmlFor="walletBackgroundColor" className="d-block mb-2">
                    Background color
                  </label>
                  <div className="wallet-color-inputs">
                    <input id="walletBackgroundColor" type="color" value={availability.backgroundColor} onChange={e => updateSetting("backgroundColor", e.target.value)} />
                    <InputText value={availability.backgroundColor} onChange={e => updateSetting("backgroundColor", e.target.value)} aria-label="Wallet background color" />
                  </div>
                </div>
                <div className="p-field">
                  <label htmlFor="walletWordingColor" className="d-block mb-2">
                    Wording color
                  </label>
                  <div className="wallet-color-inputs">
                    <input id="walletWordingColor" type="color" value={availability.wordingColor} onChange={e => updateSetting("wordingColor", e.target.value)} />
                    <InputText value={availability.wordingColor} onChange={e => updateSetting("wordingColor", e.target.value)} aria-label="Wallet wording color" />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-col-12 mb-3 wallet-branding-assets">
              <div className="wallet-branding-logos">
                <div className="p-field wallet-branding-logo">
                  <label className="d-block mb-2">Apple Wallet logo</label>
                  {availability.logoUrl && <img src={availability.logoUrl} alt="Apple Wallet logo" />}
                  <FileUpload mode="basic" name="file" accept="image/png,image/jpeg" chooseLabel="Upload Apple logo" customUpload uploadHandler={upload} auto disabled={uploading} />
                  <small>Wide logos are supported and may look better in Apple Wallet.</small>
                </div>
                <div className="p-field wallet-branding-logo wallet-google-logo">
                  <label className="d-block mb-2">Google Wallet logo</label>
                  {availability.googleLogoUrl && <img src={availability.googleLogoUrl} alt="Google Wallet logo" />}
                  <FileUpload mode="basic" name="file" accept="image/png,image/jpeg" chooseLabel="Upload Google logo" customUpload uploadHandler={uploadGoogleLogo} auto disabled={uploadingGoogleLogo} />
                  <small>Use a square logo or icon. Wide logos may look small in Google Wallet.</small>
                </div>
              </div>
              <div className="p-field wallet-pass-image-field">
                <label className="d-block mb-2">Pass image</label>
                {availability.passImageUrl && <img className="wallet-pass-image-preview" src={availability.passImageUrl} alt="Pass image" />}
                <div className="wallet-pass-image-actions mt-2">
                  <FileUpload mode="basic" name="file" accept="image/png,image/jpeg" chooseLabel="Upload pass image" customUpload uploadHandler={uploadPassImage} auto disabled={uploadingPassImage} />
                  {availability.passImageUrl && <Button label="Remove pass image" icon="pi pi-times" className="p-button-text" onClick={() => updateSetting("passImageUrl", "")} disabled={uploadingPassImage} />}
                </div>
                <small>Optional image shown on the wallet pass below/near the logo depending on Apple Wallet layout. Recommended size: 1125 x 369 px.</small>
              </div>
            </div>
            <div className="p-col-12 mb-3">
              <Button
                label="View wallet pass examples"
                icon="pi pi-eye"
                className="p-button-outlined wallet-pass-preview-button"
                onClick={() => setShowPassPreviewDialog(true)}
              />
            </div>
            <Dialog
              header="Wallet Pass Examples"
              visible={showPassPreviewDialog}
              onHide={() => setShowPassPreviewDialog(false)}
              className="wallet-pass-preview-dialog"
              style={{ width: "min(95vw, 44rem)" }}
            >
              <div className="wallet-pass-previews">
                <div className="wallet-pass-preview-column">
                  <h6>Apple Wallet example</h6>
                  <div className="wallet-pass-preview" style={{ backgroundColor: availability.backgroundColor, color: availability.wordingColor }}>
                    <div className="wallet-pass-preview-header">
                      {availability.logoUrl ? <img src={availability.logoUrl} alt="Apple Wallet logo preview" /> : <span>LOGO</span>}
                      <strong>{availability.passTitle}</strong>
                    </div>
                    {availability.passImageUrl ? <img className="wallet-pass-preview-image" src={availability.passImageUrl} alt="Pass image preview" /> : <div className="wallet-pass-preview-image-placeholder">PASS IMAGE</div>}
                    <div className="wallet-pass-preview-details">
                      <div><span>MEMBER ID</span><strong>Member number</strong></div>
                      <div><span>MAX USES</span><strong>1</strong></div>
                    </div>
                    <i className="pi pi-qrcode wallet-pass-preview-qr" aria-label="Apple Wallet QR code preview" />
                  </div>
                </div>
                <div className="wallet-pass-preview-column">
                  <h6>Google Wallet example</h6>
                  <div className="wallet-google-pass-preview" style={{ backgroundColor: availability.backgroundColor, color: availability.wordingColor }}>
                    <div className="wallet-google-pass-preview-header">
                      {availability.googleLogoUrl ? <img src={availability.googleLogoUrl} alt="Google Wallet logo preview" /> : <span>LOGO</span>}
                      <strong>{availability.passTitle}</strong>
                    </div>
                    <div className="wallet-google-pass-preview-member">
                      <span>Access Pass</span>
                      <strong>Member number</strong>
                    </div>
                    <i className="pi pi-qrcode wallet-google-pass-preview-qr" aria-label="Google Wallet QR code preview" />
                    <span className="wallet-google-pass-preview-payload">strqr-example-payload</span>
                    {availability.passImageUrl ? <img className="wallet-google-pass-preview-image" src={availability.passImageUrl} alt="Pass image preview" /> : <div className="wallet-google-pass-preview-image-placeholder">PASS IMAGE</div>}
                  </div>
                </div>
              </div>
            </Dialog>
            <div className="p-field p-col-12 mb-3 wallet-pass-limits">
              <h5>Pass Limits</h5>
              <p>
                Do you want to set the limit based on? Choose any, all, or
                none.
              </p>
              <div className="mb-3">
                <label htmlFor="limitByMaxUses">
                  <input
                    id="limitByMaxUses"
                    type="checkbox"
                    checked={availability.limitByMaxUses}
                    onChange={e =>
                      setAvailability({
                        ...availability,
                        limitByMaxUses: e.target.checked,
                        maxUsesLimit: e.target.checked
                          ? availability.maxUsesLimit
                          : null
                      })
                    }
                    className="mr-2"
                  />
                  Maximum number of uses
                </label>
                {availability.limitByMaxUses && (
                  <div className="wallet-pass-limit-row wallet-pass-uses-row mt-2">
                    <label htmlFor="maxUsesLimit">
                      Set as
                    </label>
                    <InputNumber
                      id="maxUsesLimit"
                      value={availability.maxUsesLimit}
                      min={1}
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      useGrouping={false}
                      onValueChange={e =>
                        updateSetting("maxUsesLimit", e.value)
                      }
                    />
                    <span>uses</span>
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="limitByMaxDuration">
                  <input
                    id="limitByMaxDuration"
                    type="checkbox"
                    checked={availability.limitByMaxDuration}
                    onChange={e =>
                      updateSetting("limitByMaxDuration", e.target.checked)
                    }
                    className="mr-2"
                  />
                  Maximum time
                </label>
                {availability.limitByMaxDuration && (
                  <div className="wallet-pass-limit-row mt-2">
                    <div className="wallet-pass-duration-input">
                      <InputNumber
                        id="maxDurationMinutes"
                        value={availability.durationMinutes}
                        min={0}
                        minFractionDigits={0}
                        maxFractionDigits={0}
                        useGrouping={false}
                        onValueChange={e =>
                          updateSetting("durationMinutes", e.value)
                        }
                      />
                      <label htmlFor="maxDurationMinutes">minutes</label>
                    </div>
                    <div className="wallet-pass-duration-input">
                      <InputNumber
                        id="maxDurationHours"
                        value={availability.durationHours}
                        min={0}
                        minFractionDigits={0}
                        maxFractionDigits={0}
                        useGrouping={false}
                        onValueChange={e =>
                          updateSetting("durationHours", e.value)
                        }
                      />
                      <label htmlFor="maxDurationHours">hours</label>
                    </div>
                    <div className="wallet-pass-duration-input">
                      <InputNumber
                        id="maxDurationDays"
                        value={availability.durationDays}
                        min={0}
                        minFractionDigits={0}
                        maxFractionDigits={0}
                        useGrouping={false}
                        onValueChange={e =>
                          updateSetting("durationDays", e.value)
                        }
                      />
                      <label htmlFor="maxDurationDays">days</label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-field p-col-12 mb-3">
              <label htmlFor="walletEmailSubject" className="d-block mb-2">
                Email subject
              </label>
              <InputText
                id="walletEmailSubject"
                value={availability.emailSubject}
                maxLength={200}
                onChange={e => updateSetting("emailSubject", e.target.value)}
              />
              <small>{availability.emailSubject.length}/200</small>
            </div>
            <div className="p-field p-col-12 mb-3 wallet-branding-editor">
              <label htmlFor="walletEmailBody" className="d-block mb-2">
                Email body
              </label>
              <ReactQuill
                id="walletEmailBody"
                theme="snow"
                value={availability.emailBody}
                onChange={value => updateSetting("emailBody", value)}
                modules={editorModules}
              />
              <small>{availability.emailBody.length}/4000</small>
            </div>
            <div className="p-field p-col-12 mb-3 wallet-template-help">
              <small>Supported tags: {TEMPLATE_TAGS}</small>
              <br />
              <small>Example subject: Your %location name% Wallet Pass</small>
              <br />
              <small>
                'Add to Apple Wallet' and 'Add to Android Wallet' buttons are
                added automatically.
              </small>
            </div>
            <div className="p-col-12 wallet-branding-actions">
              <Button
                label="Save"
                icon="pi pi-save"
                onClick={save}
                loading={saving}
                disabled={uploading || uploadingGoogleLogo || uploadingPassImage}
              />
              <Button
                label="Send Test"
                icon="pi pi-send"
                className="ml-2"
                onClick={() => setShowTestPassDialog(true)}
                disabled={locationsLoading}
              />
            </div>
            <Dialog
              header="Send Test Wallet Pass"
              visible={showTestPassDialog}
              onHide={() => setShowTestPassDialog(false)}
              className="wallet-test-dialog"
              style={{ width: "min(95vw, 42rem)" }}
            >
              <div className="p-fluid p-formgrid p-grid wallet-test-form">
                <div className="p-field p-col-12 mb-3">
                  <label htmlFor="testWalletLocation" className="d-block mb-2">
                    Location
                  </label>
                  <Dropdown
                    id="testWalletLocation"
                    value={testPassRequest.locationId}
                    options={locations}
                    optionLabel="name"
                    optionValue="id"
                    onChange={e =>
                      setTestPassRequest({
                        ...testPassRequest,
                        locationId: e.value
                      })
                    }
                    placeholder={
                      locationsLoading
                        ? "Loading locations..."
                        : "Select a location"
                    }
                    disabled={locationsLoading}
                  />
                </div>
                <div className="p-field p-col-12 mb-3">
                  <label htmlFor="testWalletEmail" className="d-block mb-2">
                    Email address
                  </label>
                  <InputText
                    id="testWalletEmail"
                    type="email"
                    value={testPassRequest.email}
                    onChange={e =>
                      setTestPassRequest({
                        ...testPassRequest,
                        email: e.target.value
                      })
                    }
                  />
                </div>
                <div className="p-field p-col-12 mb-3">
                  <label htmlFor="testWalletExpiry" className="d-block mb-2">
                    Expiry date/time
                  </label>
                  <InputText
                    id="testWalletExpiry"
                    type="datetime-local"
                    value={testPassRequest.expiresAtUtc}
                    onChange={e =>
                      setTestPassRequest({
                        ...testPassRequest,
                        expiresAtUtc: e.target.value
                      })
                    }
                  />
                  <small>Leave empty to use the 24-hour default.</small>
                </div>
                <div className="p-field p-col-12 mb-3">
                  <label htmlFor="testWalletOverrideLms">
                    <input
                      id="testWalletOverrideLms"
                      type="checkbox"
                      checked={testPassRequest.overrideLms}
                      onChange={e =>
                        setTestPassRequest({
                          ...testPassRequest,
                          overrideLms: e.target.checked
                        })
                      }
                      className="mr-2"
                    />
                    Override LMS
                  </label>
                </div>
                {!testPassRequest.overrideLms && (
                  <div className="p-field p-col-12 mb-3">
                    <label
                      htmlFor="testWalletMemberNumber"
                      className="d-block mb-2"
                    >
                      Member/Card Number
                    </label>
                    <InputText
                      id="testWalletMemberNumber"
                      value={testPassRequest.memberNumber}
                      onChange={e =>
                        setTestPassRequest({
                          ...testPassRequest,
                          memberNumber: e.target.value
                        })
                      }
                    />
                  </div>
                )}
                <div className="p-col-12 wallet-test-actions">
                  <Button
                    label="Send Test"
                    icon="pi pi-send"
                    onClick={sendTestPass}
                    loading={sendingTestPass}
                    disabled={locationsLoading}
                  />
                </div>
                {testPassResult && (
                  <details className="p-col-12 mt-3 wallet-test-result">
                    <summary>Test pass details</summary>
                    <div className="mt-2">
                      <div>Serial number: {testPassResult.serialNumber}</div>
                      <div>QR payload: {testPassResult.qrPayload}</div>
                      <div>Pass URL: {testPassResult.passUrl}</div>
                      <div>Expires: {testPassResult.expiresAtUtc}</div>
                    </div>
                  </details>
                )}
              </div>
            </Dialog>
            <Dialog
              visible={Boolean(saveDialog)}
              onHide={() => setSaveDialog(null)}
              className={`wallet-save-dialog wallet-save-dialog-${
                saveDialog && saveDialog.severity
              }`}
              footer={
                <Button
                  label="OK"
                  icon="pi pi-check"
                  onClick={() => setSaveDialog(null)}
                />
              }
            >
              {saveDialog && (
                <div className="wallet-save-dialog-content">
                  <i
                    className={
                      saveDialog.severity === "success"
                        ? "pi pi-check-circle"
                        : "pi pi-times-circle"
                    }
                    aria-hidden="true"
                  />
                  <div>
                    <h5>
                      {saveDialog.severity === "success"
                        ? "Settings saved"
                        : "Settings not saved"}
                    </h5>
                    <p>{saveDialog.message}</p>
                  </div>
                </div>
              )}
            </Dialog>
          </div>
        )}
      </Container>
    </Card>
  );
};
export default SupportWalletBranding;
